import React, { useState, useEffect, useCallback } from 'react';
import { X, History, ChevronLeft, ChevronRight, CheckCircle, Clock, Filter, Globe, Home, Building, User } from 'lucide-react';
import { condoApi } from '../../../services/api';
import { CustomerNote } from '../../../hooks/useCustomerNotes';
import { logger } from '../../../services/logger';
import { cn } from '../../../utils/cn';

interface NotesHistoryModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string | undefined;
  customerName?: string;
  customerType?: 'condominio' | 'empresa';
  condoType?: 'apartamento' | 'casas';
  lightTheme?: boolean;
}

type StatusFilter = 'all' | 'active' | 'concluded';
type ScopeFilter = 'all' | 'global' | 'person' | 'unit' | 'department';

/**
 * Modal displaying the complete history of customer notes
 * Shows both active and concluded notes with filtering and pagination
 * Now includes scope filter (Global/Unit/Department) and displays scope info on each note
 */
export const NotesHistoryModal: React.FC<NotesHistoryModalProps> = ({
  open,
  onClose,
  customerId,
  customerName,
  customerType = 'condominio',
  condoType = 'apartamento',
  lightTheme = false,
}) => {
  const locationLabel = customerType === 'empresa'
    ? 'Depto'
    : condoType === 'casas'
      ? 'Lote'
      : 'Unidade';

  const LocationIcon = customerType === 'empresa' ? Building : Home;
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');

  const fetchHistory = useCallback(async () => {
    if (!customerId || !open) return;

    setLoading(true);
    setError(null);

    try {
      logger.debug('NotesHistoryModal', 'Fetching history', { customerId, page, statusFilter, scopeFilter });
      const response = await condoApi.getCustomerNotesHistory(customerId, {
        page,
        limit: 10,
        status: statusFilter,
        scope: scopeFilter,
      });

      if (response.data.success) {
        setNotes(response.data.notes || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
      } else {
        throw new Error('Failed to fetch history');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch history';
      logger.error('NotesHistoryModal', 'Error fetching history', err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [customerId, open, page, statusFilter, scopeFilter]);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [fetchHistory, open]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, scopeFilter]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const getScopeBadge = (note: CustomerNote) => {
    if (note.person_id) {
      return (
        <div className={cn("flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
          lightTheme ? "bg-cyan-100 text-cyan-700 border border-cyan-200" : "bg-cyan-500/20 text-cyan-400"
        )}>
          <User className="h-3 w-3" />
          {note.person_name || 'Pessoa'}
        </div>
      );
    }
    if (note.unit_id) {
      const fallbackLabel = condoType === 'casas' ? 'Lote' : 'Unidade';
      return (
        <div className={cn("flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
          lightTheme ? "bg-orange-100 text-orange-700 border border-orange-200" : "bg-orange-500/20 text-orange-400"
        )}>
          <LocationIcon className="h-3 w-3" />
          {note.unit_name || fallbackLabel}
        </div>
      );
    }
    if (note.department_id) {
      return (
        <div className={cn("flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
          lightTheme ? "bg-blue-100 text-blue-700 border border-blue-200" : "bg-blue-500/20 text-blue-400"
        )}>
          <Building className="h-3 w-3" />
          {note.department_name || 'Departamento'}
        </div>
      );
    }
    return (
      <div className={cn("flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
        lightTheme ? "bg-blue-100 text-blue-700 border border-blue-200" : "bg-blue-500/20 text-blue-400"
      )}>
        <Globe className="h-3 w-3" />
        Global
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className={cn(
        "relative w-full max-w-2xl max-h-[80vh] rounded-xl shadow-2xl border flex flex-col",
        lightTheme ? "bg-white border-gray-200" : "bg-gray-900 border-gray-700"
      )}>
        {/* Header */}
        <div className={cn("flex items-center justify-between p-4 border-b", lightTheme ? "border-gray-200" : "border-gray-700")}>
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", lightTheme ? "bg-gray-100" : "bg-gray-700")}>
              <History className={cn("h-5 w-5", lightTheme ? "text-gray-600" : "text-gray-300")} />
            </div>
            <div>
              <h2 className={cn("text-lg font-semibold", lightTheme ? "text-gray-900" : "text-white")}>Histórico de Avisos</h2>
              {customerName && (
                <p className={cn("text-sm", lightTheme ? "text-gray-500" : "text-gray-400")}>{customerName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn("p-2 rounded-lg transition-colors", lightTheme ? "text-gray-400 hover:text-gray-700 hover:bg-gray-100" : "text-gray-400 hover:text-white hover:bg-gray-700")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filters */}
        <div className={cn("flex flex-wrap items-center gap-3 p-4 border-b", lightTheme ? "border-gray-200" : "border-gray-700")}>
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className={cn("h-4 w-4", lightTheme ? "text-gray-400" : "text-gray-500")} />
            <div className={cn("flex gap-1 rounded-lg p-1", lightTheme ? "bg-gray-100" : "bg-gray-800")}>
              {(['all', 'active', 'concluded'] as StatusFilter[]).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                    statusFilter === status
                      ? lightTheme ? "bg-white text-gray-800 shadow-sm border border-gray-200" : "bg-gray-600 text-white"
                      : lightTheme ? "text-gray-500 hover:text-gray-700 hover:bg-white/60" : "text-gray-400 hover:text-white hover:bg-gray-700"
                  )}
                >
                  {status === 'all' && 'Todos'}
                  {status === 'active' && 'Ativos'}
                  {status === 'concluded' && 'Concluídos'}
                </button>
              ))}
            </div>
          </div>

          {/* Scope Filter */}
          <div className={cn("flex gap-1 rounded-lg p-1", lightTheme ? "bg-gray-100" : "bg-gray-800")}>
            {([
              { value: 'all', label: 'Todos', icon: null },
              { value: 'global', label: 'Global', icon: Globe },
              { value: 'person', label: 'Pessoa', icon: User },
              customerType === 'empresa'
                ? { value: 'department', label: 'Depto', icon: Building }
                : { value: 'unit', label: locationLabel, icon: LocationIcon },
            ] as { value: ScopeFilter; label: string; icon: any }[]).map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setScopeFilter(value)}
                className={cn(
                  "flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                  scopeFilter === value
                    ? lightTheme ? "bg-white text-gray-800 shadow-sm border border-gray-200" : "bg-gray-600 text-white"
                    : lightTheme ? "text-gray-500 hover:text-gray-700 hover:bg-white/60" : "text-gray-400 hover:text-white hover:bg-gray-700"
                )}
              >
                {Icon && <Icon className="h-3 w-3" />}
                {label}
              </button>
            ))}
          </div>

          <span className={cn("ml-auto text-xs", lightTheme ? "text-gray-400" : "text-gray-500")}>
            {total} {total === 1 ? 'aviso' : 'avisos'}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className={cn("h-8 w-8 border-2 rounded-full animate-spin", lightTheme ? "border-gray-200 border-t-gray-600" : "border-gray-600 border-t-white")} />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
              <button
                onClick={fetchHistory}
                className={cn("mt-4 px-4 py-2 text-sm rounded-lg", lightTheme ? "text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200" : "text-gray-300 hover:text-white bg-gray-700")}
              >
                Tentar novamente
              </button>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-12">
              <History className={cn("h-12 w-12 mx-auto mb-3", lightTheme ? "text-gray-300" : "text-gray-600")} />
              <p className={cn(lightTheme ? "text-gray-400" : "text-gray-400")}>Nenhum aviso encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => {
                const created = formatDateTime(note.created_at);
                const concluded = note.concluded_at ? formatDateTime(note.concluded_at) : null;

                return (
                  <div
                    key={note.id}
                    className={cn("p-4 rounded-lg border", note.is_active
                      ? lightTheme
                        ? "bg-amber-50 border-amber-200"
                        : "bg-yellow-500/10 border-yellow-500/30"
                      : lightTheme
                        ? "bg-gray-50 border-gray-200"
                        : "bg-gray-800 border-gray-700"
                    )}
                  >
                    {/* Status Badge and Scope Badge */}
                    <div className="flex items-start gap-2 mb-2 flex-wrap">
                      {note.is_active ? (
                        <div className={cn("flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
                          lightTheme ? "bg-amber-100 text-amber-700 border border-amber-200" : "bg-yellow-500/20 text-yellow-500"
                        )}>
                          <Clock className="h-3 w-3" />
                          Ativo
                        </div>
                      ) : (
                        <div className={cn("flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
                          lightTheme ? "bg-green-100 text-green-700 border border-green-200" : "bg-green-500/20 text-green-500"
                        )}>
                          <CheckCircle className="h-3 w-3" />
                          Concluído
                        </div>
                      )}
                      {getScopeBadge(note)}
                    </div>

                    {/* Content */}
                    <p className={cn("text-sm whitespace-pre-wrap font-medium",
                      note.is_active
                        ? lightTheme ? "text-amber-900" : "text-yellow-100"
                        : lightTheme ? "text-gray-700" : "text-gray-300"
                    )}>
                      {note.content}
                    </p>

                    {/* Metadata */}
                    <div className={cn("mt-3 pt-3 border-t text-xs space-y-1",
                      lightTheme ? "border-gray-200 text-gray-400" : "border-gray-700/50 text-gray-500"
                    )}>
                      <p>
                        <span className={lightTheme ? "text-gray-500" : "text-gray-400"}>Criado por:</span>{' '}
                        <span className={cn("font-medium", lightTheme ? "text-gray-700" : "text-gray-300")}>{note.created_by_name}</span>
                        {' em '}{created.date} às {created.time}
                      </p>
                      {!note.is_active && concluded && (
                        <p>
                          <span className={lightTheme ? "text-gray-500" : "text-gray-400"}>Concluído por:</span>{' '}
                          <span className={cn("font-medium", lightTheme ? "text-gray-700" : "text-gray-300")}>{note.concluded_by_name}</span>
                          {' em '}{concluded.date} às {concluded.time}
                          {note.concluded_reason && (
                            <span className={cn("block mt-1", lightTheme ? "text-gray-500" : "text-gray-400")}>
                              Motivo: {note.concluded_reason}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={cn("flex items-center justify-between p-4 border-t", lightTheme ? "border-gray-200" : "border-gray-700")}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className={cn(
                "flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                lightTheme ? "text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200" : "text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600"
              )}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </button>
            <span className={cn("text-sm", lightTheme ? "text-gray-500" : "text-gray-400")}>
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className={cn(
                "flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                lightTheme ? "text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200" : "text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600"
              )}
            >
              Próximo
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesHistoryModal;
